// ============================================
// B356 Fase 1 (AI_154) — CONFIGURAÇÃO DA INTEGRAÇÃO COM O SNCR
// ============================================
// Fonte: Manual da API SNCR v5 (set/2026), §2 — `docs/legislacao/sncr/`.
//
// Ambiente por variável de BUILD (Vercel): REACT_APP_SNCR_AMBIENTE = "hmg" | "prd".
// Padrão "hmg" (homologação) — produção gera numeração REAL; só se liga depois
// da validação ponta a ponta (Fase 3).
//
// CNPJ: a API exige o CNPJ da empresa responsável pela plataforma
// (REACT_APP_SNCR_CNPJ). Não é segredo — vai no corpo da requisição feita pelo
// navegador do médico —, mas fica em variável para não fixar no código.

export type AmbienteSncr = "hmg" | "prd";

const BASES: Record<AmbienteSncr, string> = {
  hmg: "https://api-gateway.hmg.apps.anvisa.gov.br/api-sncr/api/v1/",
  prd: "https://api-gateway.prd.apps.anvisa.gov.br/api-sncr/api/v1/",
};

export function ambienteSncr(): AmbienteSncr {
  return process.env.REACT_APP_SNCR_AMBIENTE === "prd" ? "prd" : "hmg";
}

export function baseApiSncr(amb: AmbienteSncr = ambienteSncr()): string {
  return BASES[amb];
}

export function cnpjPlataforma(): string {
  return (process.env.REACT_APP_SNCR_CNPJ || "").replace(/\D/g, "");
}

/**
 * O login gov.br do SNCR só devolve o usuário a domínios `.br` (e a localhost em
 * desenvolvimento): "Domain Whitelist: apenas domínios .br são permitidos".
 * hepato.app NÃO serve — a página de numeração roda em hepato.app.br.
 */
export function origemAceitaPeloSncr(hostname: string): boolean {
  const h = (hostname || "").toLowerCase();
  return h.endsWith(".br") || h === "localhost" || h === "127.0.0.1";
}

export const ENDERECO_PAGINA_SNCR = "https://hepato.app.br/sncr";

// ------------------------------------------------------------
// Endereço de RETORNO do login gov.br (validação ao vivo, AI_154)
// ------------------------------------------------------------
// Com client_url = ".../sncr" o auth/token devolvia 401 "Sessão inválida" 1 s
// após a volta. A API trata o client_url como a ORIGEM do app (achado também
// documentado pelo PrescSign em homologação) e amarra o session_id a ela; a
// troca do token chega com "Origin: https://hepato.app.br" — sem o "/sncr", não
// batia. Agora: client_url = só a origem; a Anvisa devolve para
// "/?session_id=…&state=sncr" e o index.tsx encaminha para /sncr na hora.
export const ESTADO_RETORNO_SNCR = "sncr";

/** Para onde levar a volta do gov.br que caiu na raiz; null = não é volta do SNCR. */
export function destinoRetornoSncr(pathname: string, search: string): string | null {
  if (pathname !== "/") return null;
  const q = new URLSearchParams(search);
  const ehSncr =
    q.get("state") === ESTADO_RETORNO_SNCR || (q.has("session_id") && !q.has("state"));
  if (!ehSncr || !(q.has("session_id") || q.has("error"))) return null;
  return `/sncr${search}`;
}

/** Abaixo disto o Receituário avisa para renovar o bloco (AI_154). */
export const LIMIAR_SALDO_BAIXO = 50;

/** Limites da API (Manual v5 §2.3.2.5) — mostrados ao médico. */
export const LIMITES_RCE_RET = {
  numerosPorPedido: 1000,
  pedidosPorMes: 3,
  numerosPorMesPorTipo: 3000,
};

/** CNPJ válido pelos dígitos verificadores (a API rejeita CNPJ inválido). */
export function cnpjValido(cnpj: string): boolean {
  const c = (cnpj || "").replace(/\D/g, "");
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  const calc = (base: string, pesos: number[]) => {
    const soma = base
      .split("")
      .reduce((s, d, i) => s + parseInt(d, 10) * pesos[i], 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const p1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [6, ...p1];
  const d1 = calc(c.slice(0, 12), p1);
  const d2 = calc(c.slice(0, 12) + d1, p2);
  return c.endsWith(`${d1}${d2}`);
}

// ------------------------------------------------------------
// Numeração nas ASSINATURAS
// ------------------------------------------------------------
// Em PRODUÇÃO, todo documento assinado que exige número (RCE, RET) consome um
// número do cofre. Em HOMOLOGAÇÃO os números são de TESTE e não podem ir em
// receita real: só entram se o médico ligar o "modo teste" na página /sncr
// (fica neste navegador), e o documento sai com tarja de homologação.
export const CHAVE_MODO_TESTE = "sncr_modo_teste_assinatura";

// Revisão adversarial (AI_154): o modo teste EXPIRA sozinho (12 h) — ligado e
// esquecido, faria receitas reais saírem "SEM VALOR LEGAL".
export const VALIDADE_MODO_TESTE_MS = 12 * 60 * 60 * 1000;

// Validação ao vivo (AI_154): o modo teste ficava preso ao NAVEGADOR — outro
// médico que entrasse no mesmo computador via a faixa vermelha e tinha a
// assinatura de controlado bloqueada (sem numeração própria). Agora vale só para
// o USUÁRIO que o ligou; valor antigo (sem usuário) é descartado.
function lerModoTeste(): { uid: string; ligadoEm: number } | null {
  try {
    const v = JSON.parse(localStorage.getItem(CHAVE_MODO_TESTE) || "null");
    if (v && typeof v.uid === "string" && typeof v.ligadoEm === "number") return v;
    if (v !== null) localStorage.removeItem(CHAVE_MODO_TESTE);
  } catch {
    try {
      localStorage.removeItem(CHAVE_MODO_TESTE);
    } catch {
      /* sem armazenamento */
    }
  }
  return null;
}

export function numeracaoNasAssinaturas(
  uid?: string | null,
  agora: number = Date.now()
): boolean {
  if (ambienteSncr() === "prd") return true;
  const m = lerModoTeste();
  if (!m || !uid || m.uid !== uid) return false;
  if (agora - m.ligadoEm > VALIDADE_MODO_TESTE_MS) {
    try {
      localStorage.removeItem(CHAVE_MODO_TESTE);
    } catch {
      /* nada */
    }
    return false;
  }
  return true;
}

export function definirModoTeste(
  ligado: boolean,
  uid?: string | null,
  agora: number = Date.now()
): void {
  try {
    if (ligado && uid) {
      localStorage.setItem(CHAVE_MODO_TESTE, JSON.stringify({ uid, ligadoEm: agora }));
    } else {
      localStorage.removeItem(CHAVE_MODO_TESTE);
    }
  } catch {
    /* sem armazenamento: permanece desligado */
  }
}

export function formatarCnpj(cnpj: string): string {
  const c = (cnpj || "").replace(/\D/g, "");
  return c.length === 14
    ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`
    : cnpj;
}
